// Project Board (Phase 3a) pure helpers + catalogs — derived from
// PHASE3_PROJECTBOARD_INVENTORY.md (§2 tab mounts, §5 routing/title map,
// §3.8 General Info) and the live Backbone source (api/api.js,
// universal_fn_tools.js).

/**
 * Stand-in for `Dispatcher.trigger('open:' + tabName)` fired by the Backbone
 * MenuView on EVERY rail click (project mvc/view/menu.js:46-52). React only
 * needs it for the click that does NOT change the hash — see the `openTab`
 * comment in ProjectBoardPage. `detail` is the tab value.
 */
export const TAB_REOPEN_EVENT = 'ksdm:project-tab-reopen';

/**
 * Tab title map (api.js changePrjAppViewState `view_title`). Keys are the
 * ROUTABLE hash fragments: `partners`/`accounting`/`manufacturing` are
 * deliberately absent ([NEG] — their dispatcher plumbing is dead; a hash like
 * `#accounting` activates nothing in Backbone, same here).
 */
export const TAB_TITLES: Record<string, string> = {
  project: 'General Info',
  costing: 'Costing',
  pricing: 'Pricing',
  sales: 'Sales',
  shipping: 'Shipping',
  service: 'Service',
  timeline: 'Timeline',
  // dev-sync F / KA-5258 — api.js:356-357 inserts the two summary tabs into
  // `view_title` between `timeline` and `ahus`.
  ahu_components: 'AHU Components',
  ahu_accessories: 'AHU Accessories',
  ahus: 'AHU Lists',
  notes: 'Notes',
  files: 'Files',
  report_logo: 'Report Logo',
};

/**
 * The 14 static tab mounts inside #container > #tabs, in container.tmpl order
 * with their exact classes. `#accounting` and `#ahus` are never populated
 * ([NEG]/#ahus-redirect) but the mounts must exist — CSS and the .active
 * toggling key off them.
 *
 * dev-sync F / KA-5258 added `#ahu_components` and `#ahu_accessories`
 * (container.tmpl:10-11), both `col-xs-12`, between `#timeline` and `#ahus`.
 */
export const TAB_MOUNTS: ReadonlyArray<{ id: string; className: string }> = [
  { id: 'project', className: 'col-xs-12' },
  { id: 'sales', className: '' },
  { id: 'costing', className: '' },
  { id: 'pricing', className: '' },
  { id: 'accounting', className: '' },
  { id: 'shipping', className: '' },
  { id: 'service', className: '' },
  { id: 'timeline', className: 'col-xs-12' },
  { id: 'ahu_components', className: 'col-xs-12' },
  { id: 'ahu_accessories', className: 'col-xs-12' },
  { id: 'ahus', className: '' },
  { id: 'files', className: '' },
  { id: 'notes', className: '' },
  { id: 'report_logo', className: '' },
];

/**
 * Partners sub-tab roles, exact order + labels (project_main.tmpl #tab_partners).
 * Selects are named `{field}_co_id` / `{field}_contact_id`.
 */
export const PARTNER_ROLES: ReadonlyArray<{ field: string; label: string }> = [
  { field: 'customer', label: 'Customer' },
  { field: 'ordering_rep', label: 'Ordering Representative' },
  { field: 'general_contractor', label: 'General Contractor' },
  { field: 'mechanical_contractor', label: 'Mechanical Contractor' },
  { field: 'control_contractor', label: 'Control Contractor' },
  { field: 'electrical_contractor', label: 'Electrical Contractor' },
  { field: 'bldg_owner', label: 'Building Owner' },
  { field: 'architect', label: 'Architect' },
  { field: 'mechanical_engineer', label: 'Mechanical Engineer' },
  { field: 'electrical_engineer', label: 'Electrical Engineer' },
  { field: 'construction_lender', label: 'Construction Lender' },
  { field: 'construction_bonder', label: 'Construction Bonder' },
];

/** Name field validity (project_main checkField: 3–50 chars, required). */
export const isProjectNameValid = (value: string): boolean =>
  value.length >= 3 && value.length <= 50;

/**
 * fn.uni_tools.syncStorageData('project', id, name) — renames the project in
 * the localStorage `pending_reports` payload (the header's generated-reports
 * list) and dispatches the synthetic `storage` CustomEvent the header listens
 * for (parity item 42).
 */
export function syncPendingReportsProjectName(projectId: number, name: string): void {
  let pendingReports: { data: Array<Record<string, unknown>> };
  try {
    pendingReports = JSON.parse(
      window.localStorage.getItem('pending_reports') ?? ''
    ) as { data: Array<Record<string, unknown>> };
    if (!pendingReports || !Array.isArray(pendingReports.data)) {
      pendingReports = { data: [] };
    }
  } catch {
    pendingReports = { data: [] };
  }
  pendingReports.data.forEach((e) => {
    if (e.project_id && Number(e.project_id) === projectId) {
      e.project_name = name;
    }
  });
  window.localStorage.setItem('pending_reports', JSON.stringify(pendingReports));
  window.dispatchEvent(
    new CustomEvent('storage', {
      detail: {
        key: 'pending_reports',
        newValue: pendingReports,
        storageArea: window.localStorage,
        url: window.location.href,
      },
    })
  );
}

/**
 * fn.uni_tools.getContenteditableDivHTML (no `slice_last`): HTML → plain text
 * with newlines, truncated to maxlength (reflecting the truncation back into
 * the div), `'\n'`-only → ''. Used by the TextEditPopup.
 */
export function extractContenteditableText(div: HTMLElement, maxlength: number): string {
  const html = div.innerHTML;
  let text = html
    .replace(/<div><br><\/div>/g, '\n') // empty divs = newlines
    .replace(/<div>/g, '\n') // divs = newlines
    .replace(/<\/div>/g, '') // remove closing divs
    .replace(/<br\s*\/?>/gi, '\n') // convert <br> to \n
    .replace(/&nbsp;/g, ' ') // convert &nbsp; to space
    .replace(/<[^>]+>/g, ''); // strip any other tags

  if (text.length > maxlength) {
    text = text.slice(0, maxlength);
    const safeText = text
      .split('\n')
      .map((line) => (line === '' ? '<div><br></div>' : `<div>${line}</div>`))
      .join('');
    // eslint-disable-next-line no-param-reassign
    div.innerHTML = safeText;
  }
  return text === '\n' ? '' : text;
}

/**
 * project_main formatNumber: comma thousands grouping; integer → 0dp,
 * decimal/float → 2dp. Applied on render to inputs with data-formatter_type.
 */
export function formatBoardNumber(
  amount: string | number,
  format: 'integer' | 'decimal' | 'float'
): string {
  const decimalCount = format === 'integer' ? 0 : 2;
  const raw = String(amount).replace(/,/g, '');
  const num = Number(raw) || 0;
  const negativeSign = num < 0 ? '-' : '';
  const fixed = Math.abs(num).toFixed(decimalCount);
  const [intPart, fracPart] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return negativeSign + grouped + (fracPart != null ? `.${fracPart}` : '');
}

// `autofocusAttr` moved to `hooks/useAutofocusAttr` in Phase 7 T2 — it is keyed
// off by BOTH this page's popup handler and the CMS shell's, so it is not a
// project-board concern.

/**
 * Costing/Pricing `generetionTimeStamp()` (sic) — the "Generated on :" header
 * value, `yyyy.MM.dd_HH:mm:ss` (recomputed on every render, parity 156/164).
 */
export function generationTimeStamp(): string {
  const t = new Date();
  const p = (n: number): string => (n < 10 ? `0${n}` : String(n));
  return `${t.getFullYear()}.${p(t.getMonth() + 1)}.${p(t.getDate())}_${p(
    t.getHours()
  )}:${p(t.getMinutes())}:${p(t.getSeconds())}`;
}
